<template>
  <div>
    <report-panel-collapsible :report="report" :anchor="'profitloss'" :initally-open="showDiff">
      <div slot="title">Profit &amp; Loss</div>
      <div slot="body">
        <!-- panel content start -->
        <div class="table-responsive">
          <table class="table table-striped table-condensed" id="profit-loss-table">
            <thead>
              <tr>
                <th>&nbsp;</th>
                <th
                  class="text-right"
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :class="extraClasses(index, 'accounts_date')"
                >
                  {{ isEmptyMessage(formatDate(financial.accounts_date)) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <header-cell>
                  <template slot="heading">Turnover:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.turnover"
                  :percentageValue="financial.turnover_delta_percentage"
                  :direction="financial.turnover_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'turnover')"
                />
              </tr>

              <tr>
                <header-cell>
                  <template slot="heading">Cost of Sales:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.cost_of_sales"
                  :percentageValue="financial.cost_of_sales_delta_percentage"
                  :direction="financial.cost_of_sales_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'cost_of_sales')"
                />
              </tr>

              <tr class="totalRow">
                <header-cell>
                  <template slot="heading">Gross Profit:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.gross_profit"
                  :percentageValue="financial.gross_profit_delta_percentage"
                  :direction="financial.gross_profit_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'gross_profit')"
                />
              </tr>

              <tr>
                <header-cell>
                  <template slot="heading">Administrative Expenses:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.administrative_expenses"
                  :percentageValue="financial.administrative_expenses_delta_percentage"
                  :direction="financial.administrative_expenses_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'administrative_expenses')"
                />
              </tr>

              <tr>
                <header-cell>
                  <template slot="heading">Operating Profit / Loss:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.operating_profit_loss"
                  :percentageValue="financial.operating_profit_loss_delta_percentage"
                  :direction="financial.operating_profit_loss_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'operating_profit_loss')"
                />
              </tr>

              <tr>
                <header-cell>
                  <template slot="heading">Other Interests Receivable:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.other_interests_receivable"
                  :percentageValue="financial.other_interests_receivable_delta_percentage"
                  :direction="financial.other_interests_receivable_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'other_interests_receivable')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Other Interests Payable:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.other_interest_payable"
                  :percentageValue="financial.other_interest_payable_delta_percentage"
                  :direction="financial.other_interest_payable_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'other_interest_payable')"
                />
              </tr>
              <tr class="totalRow">
                <header-cell>
                  <template slot="heading">Net Profit/(Loss) Before Tax:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.net_profit_before_tax"
                  :percentageValue="financial.net_profit_before_tax_delta_percentage"
                  :direction="financial.net_profit_before_tax_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'net_profit_before_tax')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Tax:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.tax"
                  :percentageValue="financial.tax_delta_percentage"
                  :direction="financial.tax_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'tax')"
                />
              </tr>
              <tr class="totalRow">
                <header-cell>
                  <template slot="heading">Net Profit(Loss) After Tax:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.net_profit_or_loss_after_tax"
                  :percentageValue="financial.net_profit_or_loss_after_tax_delta_percentage"
                  :direction="financial.net_profit_or_loss_after_tax_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'net_profit_or_loss_after_tax')"
                />
              </tr>
            </tbody>
          </table>
        </div>
        <!-- panel content end -->
      </div>
    </report-panel-collapsible>
  </div>
</template>

<script>
import toolsMixin from '@/components/reports/ToolsMixin';
import ReportPanelCollapsible from '@/components/reports/components/ReportPanelCollapsible';
import HeaderCell from '@/components/reports/panels/financials/tablecells/HeaderCell';
import StandardCell from '@/components/reports/panels/financials/tablecells/StandardCell';
import financialsMixin from './FinancialsMixin';

export default {
  components: {
    ReportPanelCollapsible,
    HeaderCell,
    StandardCell,
  },
  mixins: [financialsMixin, toolsMixin],
};
</script>
